import { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import useFastNav, { recordVisit } from '../../hooks/useFastNav'
import useTranslation from '../../hooks/useTranslation'
import useOutsideClick from '../../hooks/useOutsideClick'
import { Icon_Search, Icon_Close, resolveIcon } from '../../styles/icons'

// ─── Styled Components ───────────────────────────────────────────────────────

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1050;
    background: ${({ theme }) => theme.modal.backdrop};
    opacity: 0.4;
`

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1055;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 10vh;
`

const Panel = styled.div`
    width: 560px;
    max-width: calc(100vw - 2rem);
    max-height: 480px;
    display: flex;
    flex-direction: column;
    background: ${({ theme }) => theme.modal.background};
    border: 1px solid ${({ theme }) => theme.modal.border};
    border-radius: 0.5rem;
    box-shadow: 0 0.5rem 1.5rem ${({ theme }) => theme.modal.shadow_m};
    overflow: hidden;
`

const SearchRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid ${({ theme }) => theme.modal.divider};
    flex-shrink: 0;
    color: ${({ theme }) => theme.font.label};
`

const SearchInput = styled.input`
    flex: 1;
    border: none;
    background: transparent;
    outline: none;
    font-size: var(--font-M, 1rem);
    color: ${({ theme }) => theme.font.color};
    &::placeholder { color: ${({ theme }) => theme.font.label}; }
`

const ResultList = styled.div`
    overflow-y: auto;
    flex: 1;
`

const SectionLabel = styled.div`
    padding: 0.5rem 1rem 0.2rem;
    font-size: var(--font-XS, 0.75rem);
    color: ${({ theme }) => theme.font.label};
    text-transform: uppercase;
    letter-spacing: 0.06em;
`

const Divider = styled.div`
    border-top: 1px solid ${({ theme }) => theme.modal.divider};
    margin-top: 0.25rem;
`

const Item = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 1rem;
    cursor: pointer;
    background: ${({ $active, theme }) => $active ? theme.card.active : 'transparent'};
    color: ${({ $active, theme }) => $active ? theme.btn.primary : theme.font.color};
    font-size: var(--font-S, 0.875rem);
    transition: background 0.08s;

    svg { flex-shrink: 0; }

    &:hover {
        background: ${({ theme }) => theme.card.active};
    }
`

const ShortcutChar = styled.u`
    text-decoration-thickness: 1px;
`

const CloseButton = styled.button`
    display: flex;
    align-items: center;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: ${({ theme }) => theme.font.label};
    font-size: 1.25rem;
    line-height: 1;
    flex-shrink: 0;
    &:hover { color: ${({ theme }) => theme.font.color}; }
`

const EmptyState = styled.div`
    padding: 2.5rem 1rem;
    text-align: center;
    color: ${({ theme }) => theme.font.label};
    font-size: var(--font-S, 0.875rem);
`

// ─── Helpers ─────────────────────────────────────────────────────────────────

function LabelWithShortcut({ label, shortcut, show }) {
    if (!show || !shortcut) return <span>{label}</span>

    const idx = label.toUpperCase().indexOf(shortcut.toUpperCase())
    if (idx === -1) return <span>{label}</span>

    return (
        <span>
            {label.slice(0, idx)}
            <ShortcutChar>{label.slice(idx, idx + 1)}</ShortcutChar>
            {label.slice(idx + 1)}
        </span>
    )
}

// ─── Component ───────────────────────────────────────────────────────────────

function FastNavModal({ onClose }) {
    const [query, setQuery] = useState('')
    const [highlightedIndex, setHighlightedIndex] = useState(0)
    const searchRef = useRef(null)
    const panelRef = useRef(null)
    const itemRefs = useRef([])
    const navigate = useNavigate()
    const { t } = useTranslation()

    useOutsideClick(panelRef, onClose, 'modal')

    const { topItems, allItems, hasTop } = useFastNav(query)
    const allVisible = [...topItems, ...allItems]

    // Auto-focus on open
    useEffect(() => { searchRef.current?.focus() }, [])

    // Reset highlight when results change
    useEffect(() => { setHighlightedIndex(0) }, [query])

    // Scroll highlighted item into view
    useEffect(() => {
        itemRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' })
    }, [highlightedIndex])

    const handleGoTo = (item) => {
        recordVisit(item.path)
        navigate(item.path)
        onClose()
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            onClose()
        } else if (e.key === 'ArrowDown') {
            e.preventDefault()
            setHighlightedIndex(i => Math.min(i + 1, allVisible.length - 1))
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setHighlightedIndex(i => Math.max(i - 1, 0))
        } else if (e.key === 'Enter') {
            e.preventDefault()
            const item = allVisible[highlightedIndex]
            if (item) handleGoTo(item)
        } else if (!query && e.altKey && e.key.length === 1 && /[a-zA-Z]/.test(e.key)) {
            e.preventDefault()
            const item = allVisible.find(i => i.shortcut === e.key.toUpperCase())
            if (item) handleGoTo(item)
        }
    }

    const showShortcuts = !query

    return (
        <>
            <Backdrop />
            <Overlay>
                <Panel ref={panelRef} onKeyDown={handleKeyDown}>
                    <SearchRow>
                        {Icon_Search}
                        <SearchInput
                            ref={searchRef}
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            placeholder={t('fastNav.search.placeholder') || 'Navigate to…'}
                        />
                        <CloseButton onClick={onClose}>{Icon_Close}</CloseButton>
                    </SearchRow>

                    <ResultList>
                        {allVisible.length === 0 && (
                            <EmptyState>
                                {t('fastNav.search.empty') || `No results for "${query}"`}
                            </EmptyState>
                        )}

                        {allVisible.length > 0 && !query && hasTop && (
                            <SectionLabel>{t('fastNav.section.top') || 'Top'}</SectionLabel>
                        )}

                        {topItems.map((item, i) => (
                            <Item
                                key={item.key}
                                ref={el => itemRefs.current[i] = el}
                                $active={i === highlightedIndex}
                                onClick={() => handleGoTo(item)}
                                onMouseEnter={() => setHighlightedIndex(i)}
                            >
                                {resolveIcon(item.icon)}
                                <LabelWithShortcut
                                    label={item.label}
                                    shortcut={item.shortcut}
                                    show={showShortcuts}
                                />
                            </Item>
                        ))}

                        {allVisible.length > 0 && !query && (
                            <>
                                {hasTop && <Divider />}
                                <SectionLabel>{t('fastNav.section.all') || 'All'}</SectionLabel>
                            </>
                        )}

                        {allItems.map((item, i) => {
                            const absIdx = topItems.length + i
                            return (
                                <Item
                                    key={item.key}
                                    ref={el => itemRefs.current[absIdx] = el}
                                    $active={absIdx === highlightedIndex}
                                    onClick={() => handleGoTo(item)}
                                    onMouseEnter={() => setHighlightedIndex(absIdx)}
                                >
                                    {resolveIcon(item.icon)}
                                    <LabelWithShortcut
                                        label={item.label}
                                        shortcut={item.shortcut}
                                        show={showShortcuts}
                                    />
                                </Item>
                            )
                        })}
                    </ResultList>
                </Panel>
            </Overlay>
        </>
    )
}

export default FastNavModal
